import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SearchIcon, StarIcon, UsersIcon, CheckCircleIcon, ArrowRightIcon, TargetIcon, TrendingUpIcon, BarChart3Icon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
export function AdCampaignsPage() {
  return <div className="bg-viridia-black min-h-screen" data-id="element-315">
      <SEOHead title="Yelp Ads Partner Agency | Ad Campaign Management | Viridia Analytics Phoenix" description="Expert Yelp Ads campaign management for Phoenix local businesses. As an official Yelp Ads Partner Agency, we maximize your ad spend and drive qualified leads." canonical="/ad-campaigns" keywords="Yelp Ads management Phoenix, Yelp Ads partner agency, local business advertising, ad campaign optimization, lead generation Phoenix" data-id="element-316" />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden min-h-[90vh] flex items-center" data-id="element-317">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-318">
          <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-viridia/20 blur-[120px] animate-pulse-slow" data-id="element-319"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-320">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-321">
            {/* Hero Content */}
            <div className="max-w-2xl" data-id="element-322">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} data-id="element-323">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 border border-viridia/20 text-viridia text-sm font-semibold tracking-wide mb-6" data-id="element-324">
                  PAID ADVERTISING
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6" data-id="element-325">
                  Your Yelp Ads{' '}
                  <span className="text-viridia" data-id="element-326">Partner Agency</span>
                </h1>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-327">
                  Leverage our insider expertise from former Yelp employees to
                  drive qualified leads. We understand CPC optimization,
                  competitive bidding, and seasonality trends that maximize ROI
                  on Yelp for local service businesses.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-328">
                  <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-viridia text-white font-semibold hover:bg-viridia-dark transition-colors duration-300" data-id="element-329">
                    Book a Free Ad Strategy Call
                  </Link>
                  <Link to="/pricing" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border border-gray-600 text-white font-semibold hover:bg-white/5 transition-colors duration-300" data-id="element-330">
                    View Pricing
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Hero Illustration */}
            <div className="relative h-[400px] lg:h-[500px] hidden md:block" data-id="element-331">
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} className="absolute inset-0 flex items-center justify-center" data-id="element-332">
                {/* Central Yelp Partner Badge */}
                <motion.div className="relative z-30 flex flex-col items-center" animate={{
                y: [0, -15, 0]
              }} transition={{
                duration: 6,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-333">
                  <img src="https://cdn.magicpatterns.com/uploads/8by3tNpc2QB7RjdKpneoAS/yelppartner.webp" alt="Official Yelp Advertising Partner" className="w-80 h-80 object-contain drop-shadow-[0_0_40px_rgba(239,68,68,0.4)]" data-id="element-334" />
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* PLATFORMS SECTION */}
      <section className="py-24 bg-viridia-light relative" data-id="element-335">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-336">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-337">
            <div data-id="element-338">
              <ScrollReveal data-id="element-339">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-6 border border-viridia/20" data-id="element-340">
                  PLATFORM EXPERTISE
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-341">
                  Why Yelp Ads?
                </h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-342">
                  Yelp is where high-intent customers go when they are ready to
                  hire a local service professional. We don't just set and
                  forget. We actively manage your campaigns using insider
                  knowledge to maximize your return on ad spend.
                </p>
                <div className="space-y-6" data-id="element-343">
                  {['Reach customers actively searching for your services', 'Target specific local neighborhoods and zip codes', 'Optimize Cost-Per-Click (CPC) to stretch your budget', 'Leverage our Partner Agency priority support'].map((feature, index) => <div key={index} className="flex items-start" data-id="element-344">
                      <CheckCircleIcon className="w-6 h-6 text-viridia mr-4 flex-shrink-0 mt-0.5" data-id="element-345" />
                      <span className="text-gray-700 font-medium text-lg" data-id="element-346">
                        {feature}
                      </span>
                    </div>)}
                </div>
              </ScrollReveal>
            </div>

            <div className="relative" data-id="element-347">
              <ScrollReveal direction="left" data-id="element-348">
                <div className="bg-white rounded-2xl p-10 shadow-xl border-t-4 border-red-500 relative overflow-hidden" data-id="element-349">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-bl-full -z-10" data-id="element-350"></div>
                  <div className="w-16 h-16 rounded-xl bg-red-500/10 flex items-center justify-center text-red-500 mb-8" data-id="element-351">
                    <StarIcon className="w-8 h-8 fill-current" data-id="element-352" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-353">
                    Targeted Local Advertising
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-lg mb-8" data-id="element-354">
                    Reach customers already looking for businesses like yours on
                    Yelp. Our team includes former Yelp insiders who know
                    exactly how to maximize your ad spend on the platform.
                  </p>
                  <Link to="/contact" className="inline-flex items-center text-red-500 font-bold hover:text-red-600 transition-colors text-lg" data-id="element-355">
                    Start Dominating Yelp{' '}
                    <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-356" />
                  </Link>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-357">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-358">
          <ScrollReveal data-id="element-359">
            <div className="mb-16" data-id="element-360">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-361">
                What You Get
              </h2>
              <p className="text-lg text-gray-400 max-w-2xl" data-id="element-362">
                A comprehensive approach to paid advertising that covers every
                angle of your campaigns.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8" data-id="element-363">
            {[{
            title: 'Campaign Strategy & Setup',
            desc: 'Custom-built campaigns tailored to your specific business goals and target audience.'
          }, {
            title: 'Ad Creative & Copywriting',
            desc: 'Compelling ad copy and visuals designed to capture attention and drive clicks.'
          }, {
            title: 'Keyword Research & Targeting',
            desc: 'In-depth research to find the most profitable search terms for your services.'
          }, {
            title: 'Bid Management & Optimization',
            desc: 'Continuous monitoring and adjustment of bids to maximize your return on ad spend.'
          }, {
            title: 'A/B Testing & Performance Tuning',
            desc: 'Rigorous testing of different ad variations to find the highest converting combinations.'
          }, {
            title: 'Monthly Reporting & Strategy Calls',
            desc: 'Transparent reporting on key metrics and regular calls to discuss strategy and results.'
          }, {
            title: 'Landing Page Recommendations',
            desc: 'Expert advice on optimizing your website to convert the traffic we send.'
          }, {
            title: 'Competitor Analysis',
            desc: "Ongoing monitoring of your competitors' ad strategies to keep you one step ahead."
          }].map((feature, index) => <ScrollReveal key={index} delay={index * 0.05} data-id="element-364">
                <div className="flex items-start" data-id="element-365">
                  <CheckCircleIcon className="w-6 h-6 text-viridia mt-1 flex-shrink-0 mr-4" data-id="element-366" />
                  <div data-id="element-367">
                    <h4 className="text-lg font-semibold text-white mb-1" data-id="element-368">
                      {feature.title}
                    </h4>
                    <p className="text-gray-400" data-id="element-369">{feature.desc}</p>
                  </div>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* RESULTS SECTION */}
      <section className="py-24 bg-viridia-surface relative overflow-hidden" data-id="element-370">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-viridia/5 blur-[100px] pointer-events-none" data-id="element-371"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-372">
          <ScrollReveal data-id="element-373">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-374">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-375">
                Why Businesses Choose Us for Ads
              </h2>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-376">
            <ScrollReveal delay={0.1} data-id="element-377">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8 h-full" data-id="element-378">
                <div className="w-12 h-12 rounded-full bg-viridia/10 flex items-center justify-center text-viridia mb-6" data-id="element-379">
                  <TargetIcon className="w-6 h-6" data-id="element-380" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-381">
                  Former Platform Insiders
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-382">
                  Our team includes former employees from Yelp who know the
                  algorithms inside and out. We use this insider knowledge to
                  give you an unfair advantage over your local competitors.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-383">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8 h-full" data-id="element-384">
                <div className="w-12 h-12 rounded-full bg-viridia/10 flex items-center justify-center text-viridia mb-6" data-id="element-385">
                  <TrendingUpIcon className="w-6 h-6" data-id="element-386" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-387">
                  ROI-Focused Approach
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-388">
                  Every dollar of your ad spend is optimized. We track
                  cost-per-lead, not just clicks. If a campaign isn't generating
                  profitable leads, we pivot quickly.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.3} data-id="element-389">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8 h-full" data-id="element-390">
                <div className="w-12 h-12 rounded-full bg-viridia/10 flex items-center justify-center text-viridia mb-6" data-id="element-391">
                  <BarChart3Icon className="w-6 h-6" data-id="element-392" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-393">
                  Full-Funnel Tracking
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-394">
                  From ad click to booked job, we track the entire customer
                  journey so you know exactly what's working. We connect your
                  ads directly to your CRM for true revenue attribution.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia-dark to-viridia relative overflow-hidden" data-id="element-395">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-396"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-397">
          <ScrollReveal data-id="element-398">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-399">
              Ready to Turn Ad Spend Into Revenue?
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-400">
              Book a free strategy call and we'll audit your current campaigns —
              or build a plan from scratch.
            </p>
            <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-white text-viridia-dark font-bold text-lg hover:bg-gray-100 transition-colors duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1" data-id="element-401">
              Book Your Free Ad Strategy Call
              <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-402" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}